import React, { useEffect, useState } from "react";
import { Card, Collapse, Tag, Tooltip } from "antd";
import { RightOutlined } from "@ant-design/icons";
import {
  displayAuditValue,
  formatAuditFieldLabel,
  formatAuditTimestamp,
  formatAuditTypeLabel,
  getFieldAuditNote,
  getWeightDiffNote,
  isWeightAudit,
} from "../../utils/auditDisplay";
import actualWeightIcon from "@innofulfill/hubops-shipment-tracking/dist/assets/image/actualWeight.svg";
import volumetricWeightIcon from "@innofulfill/hubops-shipment-tracking/dist/assets/image/volumetric-weight.svg";
import dimensionIcon from "@innofulfill/hubops-shipment-tracking/dist/assets/image/dimension.svg";
import auditInfoIcon from "@innofulfill/hubops-shipment-tracking/dist/assets/image/shipmentInfo.svg";
import "./style.scss";

const AuditMetric = ({ icon, label, value, isAudit = false }) => (
  <div className="ho-kv ho-kv--withTag">
    <span
      className="ho-kv__label_audit"
      style={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        fontSize: "14px",
        fontWeight: 500,
        fontFamily: "Roboto",
      }}
    >
      {icon && <img src={icon} alt="" className="ho-kv__icon" />}
      {label}
    </span>
    <span className={isAudit ? "ho-kv__tag_audit" : "ho-kv__tag"}>
      {displayAuditValue(value)}
    </span>
  </div>
);

const WeightValueGrid = ({ record, valueType }) => {
  const prefix = valueType === "booked" ? "booked" : "audited";
  const volumetricKey =
    valueType === "booked" ? "volumetricBookedValue" : "volumetricAuditedValue";

  return (
    <div className="ho-grid">
      <AuditMetric
        label="Actual"
        value={record?.[`${prefix}Weight`]}
        isAudit
        icon={actualWeightIcon}
      />
      <AuditMetric
        label="Volumetric"
        value={record?.[volumetricKey]}
        isAudit
        icon={volumetricWeightIcon}
      />
      <AuditMetric
        label="LWH(cm)"
        value={`${displayAuditValue(record?.[`${prefix}Length`])},${displayAuditValue(
          record?.[`${prefix}Width`]
        )},${displayAuditValue(record?.[`${prefix}Height`])}`}
        icon={dimensionIcon}
      />
    </div>
  );
};

const FieldValueGrid = ({ record, valueType }) => (
  <div className="ho-grid">
    <AuditMetric
      label={formatAuditFieldLabel(record?.fieldName)}
      value={valueType === "booked" ? record?.bookedValue : record?.auditedValue}
      isAudit
    />
  </div>
);

const isXRayAudit = (record) =>
  (record?.auditType || "").toString().toUpperCase() === "X-RAY";

const parseAuditMetadata = (metadata) => {
  if (!metadata) {
    return {};
  }
  if (typeof metadata === "object") {
    return metadata;
  }
  try {
    return JSON.parse(metadata) || {};
  } catch (e) {
    return {};
  }
};

const XRayValueGrid = ({ record }) => {
  const meta = parseAuditMetadata(record?.metadata);
  const dgType = meta.dgType || "—";
  const dgClass = dgType === "DG" ? displayAuditValue(meta.dgClass) : "—";

  return (
    <>
      <div className="ho-grid">
        <AuditMetric label="Type" value={dgType} isAudit />
        <AuditMetric label="DG Class" value={dgClass} isAudit />
      </div>
      <div className="ho-grid">
        <AuditMetric label="Emp Id" value={meta.auditedByEmpId} isAudit />
        <AuditMetric label="Emp Name" value={meta.auditedByEmpName} isAudit />
      </div>
      {meta.remarks && (
        <div className="ho-note">
          <span style={{ color: "#000", fontWeight: 600 }}>Remarks:</span>{" "}
          {meta.remarks}
        </div>
      )}
    </>
  );
};

const AuditEventCard = ({ record }) => {
  const xrayAudit = isXRayAudit(record);
  const weightAudit = !xrayAudit && isWeightAudit(record);
  let note = null;
  if (!xrayAudit) {
    note = weightAudit ? getWeightDiffNote(record) : getFieldAuditNote(record);
  }
  const hubName = record?.auditedHubName || record?.sourceHubName || "—";
  const hubDisplay =
    hubName && hubName.length > 7 ? `${hubName.substring(0, 7)}...` : hubName;

  return (
    <Card className="ho-subcard ho-mt-10 ho-audit-inner" bordered={false}>
      <div className="ho-audit-header-grid">
        <div className="ho-kv-stack">
          <span className="ho-stat__label">Audited By</span>
          <Tooltip title={record?.auditMethod || "—"}>
            <span className="ho-stat__value--audit">
              {displayAuditValue(record?.auditMethod)}
            </span>
          </Tooltip>
        </div>
        <div className="ho-kv-stack">
          <span className="ho-stat__label">Hub Name</span>
          <Tooltip title={hubName}>
            <span className="ho-stat__value--audit">{hubDisplay}</span>
          </Tooltip>
        </div>
        <div className="ho-kv-stack">
          <span className="ho-stat__label">Audited Date</span>
          <span className="ho-stat__value--audit">
            {formatAuditTimestamp(record?.auditTimestamp)}
          </span>
        </div>
      </div>

      <div style={{ height: "12px" }} />

      <div className="ho-subtitle-audit">
        {formatAuditTypeLabel(record?.auditType)}
      </div>

      {xrayAudit ? (
        <div className="ho-box ho-box--flush">
          <XRayValueGrid record={record} />
        </div>
      ) : (
        <>
          <div className="ho-box ho-box--flush">
            <div className="ho-subtitle-audit">Booked Value</div>
            {weightAudit ? (
              <WeightValueGrid record={record} valueType="booked" />
            ) : (
              <FieldValueGrid record={record} valueType="booked" />
            )}
          </div>

          <div className="ho-box ho-box--flush">
            <div className="ho-subtitle-audit">Audited Value</div>
            {weightAudit ? (
              <WeightValueGrid record={record} valueType="audited" />
            ) : (
              <FieldValueGrid record={record} valueType="audited" />
            )}
          </div>
        </>
      )}

      {note && (
        <div className="ho-note">
          <span style={{ color: "#000", fontWeight: 600 }}>Note:</span> {note}
        </div>
      )}
    </Card>
  );
};

const { Panel } = Collapse;

const groupAuditsByType = (auditInfo) => {
  const groups = [];
  const groupIndexByType = new Map();

  auditInfo.forEach((record) => {
    const type = (record?.auditType || "").toString().toUpperCase() || "WEIGHT";
    if (!groupIndexByType.has(type)) {
      groupIndexByType.set(type, groups.length);
      groups.push({ type, label: formatAuditTypeLabel(record?.auditType), records: [] });
    }
    groups[groupIndexByType.get(type)].records.push(record);
  });

  return groups;
};

const AuditHistoryPanel = ({ auditInfo = [], showWhenEmpty = false, inline = false }) => {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (auditInfo.length > 0) {
      setExpanded(true);
    }
  }, [auditInfo]);

  if (!auditInfo.length && !showWhenEmpty) {
    return null;
  }

  const panelBody = (
    <>
      <div className="ho-section-title">
        <div className="ho-title-left">
          <img src={auditInfoIcon} className="ho-title-icon" alt="" />
          <span className="ho-title">Audit Info</span>
        </div>
        {auditInfo.length > 0 && (
          <Tag color="#E9F2FF" className="ho-chip">
            {auditInfo.length} Audit{auditInfo.length !== 1 ? "s" : ""}
          </Tag>
        )}
      </div>

      <div
        className="ho-list"
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          overflow: "hidden",
        }}
      >
        {!auditInfo.length && (
          <div className="audit-history-panel__empty">No audit info available</div>
        )}

        {auditInfo.length > 0 && (
          <>
            <button
              type="button"
              className="ho-list__item"
              onClick={() => setExpanded((prev) => !prev)}
            >
              <span className="ho-strong">Audit({auditInfo.length})</span>
              <span className={`ho-list__chevron ${expanded ? "ho-open" : ""}`}>▾</span>
            </button>

            {expanded && (
              <div
                className="ho-expand-block"
                style={{
                  flex: 1,
                  overflowY: "auto",
                  maxHeight: "none",
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                <Collapse
                  ghost
                  expandIcon={({ isActive }) => <RightOutlined rotate={isActive ? 90 : 0} />}
                  className="ho-audit-type-collapse"
                >
                  {groupAuditsByType(auditInfo).map((group) => (
                    <Panel
                      key={group.type}
                      className="ho-audit-type-panel"
                      header={
                        <div className="ho-audit-type-header">
                          <span className="ho-audit-type-label">{group.label}</span>
                          <Tag color="#E9F2FF" className="ho-chip">
                            {group.records.length}
                          </Tag>
                        </div>
                      }
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "10px",
                        }}
                      >
                        {group.records.map((record, index) => (
                          <AuditEventCard
                            key={`audit-${group.type}-${index}-${record?.auditTimestamp || index}`}
                            record={record}
                          />
                        ))}
                      </div>
                    </Panel>
                  ))}
                </Collapse>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );

  if (inline) {
    return <div className="audit-history-panel audit-history-panel--inline">{panelBody}</div>;
  }

  return (
    <Card className="audit-history-panel" title={null} bordered={false}>
      {panelBody}
    </Card>
  );
};

export default AuditHistoryPanel;
